import { useEffect, useMemo, useState } from 'react';
import { BrainCircuit, CheckCircle2, Circle, LoaderCircle, Sparkles } from 'lucide-react';

const DEFAULT_STEPS_BY_PHASE = {
  bootstrapping: [
    { id: 'session', label: 'Start session', status: 'active' },
    { id: 'context', label: 'Load context', status: 'pending' },
    { id: 'question', label: 'Prepare first question', status: 'pending' },
  ],
  question: [
    { id: 'context', label: 'Read context', status: 'active' },
    { id: 'question', label: 'Generate question', status: 'pending' },
    { id: 'deliver', label: 'Deliver to chat', status: 'pending' },
  ],
  analysis: [
    { id: 'answer', label: 'Analyze answer', status: 'active' },
    { id: 'bars', label: 'Update BARS scales', status: 'pending' },
    { id: 'next', label: 'Prepare next step', status: 'pending' },
  ],
  report: [
    { id: 'signals', label: 'Collect interview signals', status: 'active' },
    { id: 'summary', label: 'Generate interview summary', status: 'pending' },
    { id: 'report', label: 'Finalize report', status: 'pending' },
  ],
};

function resolveSteps(status) {
  if (Array.isArray(status?.steps) && status.steps.length) {
    return status.steps;
  }
  return DEFAULT_STEPS_BY_PHASE[status?.phase] || DEFAULT_STEPS_BY_PHASE.question;
}

function resolveTargetProgress(steps, explicitProgress, isComplete) {
  if (Number.isFinite(explicitProgress)) {
    return Math.max(0, Math.min(100, explicitProgress));
  }
  if (!steps.length) {
    return isComplete ? 100 : 24;
  }

  const completedCount = steps.filter((step) => step.status === 'completed').length;
  const activeCount = steps.filter((step) => step.status === 'active').length;
  if (isComplete) return 100;
  return Math.min(94, 18 + completedCount * 28 + activeCount * 16);
}

export default function AiProcessingPopup({ isOpen, status }) {
  const steps = useMemo(() => resolveSteps(status), [status]);
  const isComplete = status?.state === 'success';
  const targetProgress = useMemo(
    () => resolveTargetProgress(steps, status?.progress, isComplete),
    [isComplete, status?.progress, steps],
  );
  const progressKey = `${status?.phase || 'default'}:${status?.title || ''}:${steps.map((step) => `${step.id}:${step.status}`).join('|')}`;
  const [progress, setProgress] = useState(14);

  useEffect(() => {
    if (!isOpen) return undefined;

    setProgress((prev) => (progressKey ? Math.max(14, Math.min(prev, targetProgress)) : 14));
    const intervalId = globalThis.setInterval(() => {
      setProgress((prev) => {
        if (prev === targetProgress) return prev;
        if (prev < targetProgress) {
          const next = prev + Math.max(2, Math.round((targetProgress - prev) * 0.22));
          return Math.min(targetProgress, next);
        }
        return targetProgress;
      });
    }, 120);

    return () => globalThis.clearInterval(intervalId);
  }, [isOpen, progressKey, targetProgress]);

  if (!isOpen) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[110] flex items-end justify-center px-4 pb-28 sm:items-center sm:pb-0">
      <div className="w-full max-w-md overflow-hidden rounded-[28px] border border-white/70 bg-[#FFF8F1]/95 shadow-[0_24px_80px_rgba(180,83,9,0.18)] backdrop-blur-xl">
        <div className="relative">
          <div className={`absolute inset-x-0 top-0 h-1.5 ${isComplete ? 'bg-gradient-to-r from-emerald-300 via-emerald-400 to-sky-400' : 'bg-gradient-to-r from-orange-200 via-amber-300 to-orange-400'}`} />

          <div className="px-5 pb-5 pt-6 sm:px-6">
            <div className="mb-4 flex items-start gap-3">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg ${isComplete ? 'bg-gradient-to-br from-emerald-500 to-sky-500 shadow-emerald-200/70' : 'bg-gradient-to-br from-orange-500 to-amber-400 shadow-orange-200/70'}`}>
                <BrainCircuit className="h-6 w-6" />
              </div>

              <div className="min-w-0">
                <div className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] ${isComplete ? 'text-emerald-600' : 'text-orange-500'}`}>
                  <Sparkles className="h-3.5 w-3.5" />
                  AI Interview
                </div>
                <h3 className="mt-1 text-base font-semibold text-slate-900">
                  {status?.title || 'Preparing response'}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  {status?.description || 'Please wait a moment while we process your request.'}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-orange-100/80 bg-white/80 p-3">
              <div className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                <span>{status?.label || 'In progress'}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[#F3E5D4]">
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ease-out ${isComplete ? 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-sky-400' : 'bg-gradient-to-r from-orange-500 via-amber-400 to-orange-300'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {steps.map((step) => {
                const isStepComplete = step.status === 'completed';
                const isStepActive = step.status === 'active';

                return (
                  <div
                    key={step.id || step.label}
                    className={`rounded-2xl border px-3.5 py-3 transition-all ${
                      isStepComplete
                        ? 'border-emerald-200 bg-emerald-50/90'
                        : isStepActive
                          ? 'border-sky-200 bg-sky-50/90 shadow-sm'
                          : 'border-white/70 bg-white/80'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                        isStepComplete
                          ? 'bg-white text-sky-600'
                          : isStepActive
                            ? 'bg-sky-100 text-sky-600'
                            : 'bg-slate-100 text-slate-400'
                      }`}>
                        {isStepComplete ? (
                          <CheckCircle2 className="h-4.5 w-4.5" />
                        ) : isStepActive ? (
                          <LoaderCircle className="h-4.5 w-4.5 animate-spin" />
                        ) : (
                          <Circle className="h-4.5 w-4.5" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className={`text-[13px] font-semibold ${
                          isStepComplete ? 'text-emerald-700' : isStepActive ? 'text-sky-700' : 'text-slate-500'
                        }`}>
                          {step.label}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
                          {isStepComplete ? 'Completed' : isStepActive ? 'In progress' : 'Pending'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
